import type React from 'react';
import { useEffect, useRef, useState } from 'react';

import _ from 'lodash';

import {
  type BudgetTemplateLine,
  Frequency,
  FrequencyLabels,
} from 'loot-core/src/shared/budget-template';

import { SvgCheveronDown } from '../../../icons/v1';
import { type CSSProperties } from '../../../style';
import { Button } from '../../common/Button';
import { Menu } from '../../common/Menu';
import { View } from '../../common/View';
import { Tooltip } from '../../tooltips';

export type IFrequencyDropdownProps = {
  containerStyle?: CSSProperties;
  budget: Partial<BudgetTemplateLine>;
  isFocused?: boolean;
  onChange: (frequency: Frequency) => void;
};

export default function FrequencyDropdown({
  containerStyle,
  budget,
  isFocused,
  onChange,
}: IFrequencyDropdownProps) {
  const [open, setOpen] = useState(false);
  const dropdownBtnRef: React.MutableRefObject<HTMLButtonElement> = useRef();

  useEffect(() => {
    setFocus();
  }, [isFocused]);

  return (
    <View
      style={containerStyle}
      autoFocus={isFocused}
      onKeyDown={e => {
        if (e.code === 'Space') {
          setOpen(false);
          setFocus();
        } else if (e.code === 'Enter') {
          e.stopPropagation();
          setOpen(!open);
          open && setFocus();
        }
      }}
    >
      <Button
        type="bare"
        aria-label="Frequency"
        data-adoption-id="frequency-btn"
        ref={dropdownBtnRef}
        onClick={e => {
          e.stopPropagation();
          setOpen(true);
        }}
        style={{
          minWidth: '70px',
          gap: '2px',
          justifyContent: 'space-between',
        }}
      >
        <span style={{ whiteSpace: 'pre-wrap', lineHeight: '11px', flex: 1 }}>
          {budget.frequency ? FrequencyLabels[budget.frequency] : ''}
        </span>
        <SvgCheveronDown
          width={14}
          height={14}
          className="hover-visible"
          style={{ opacity: 1 }}
        />
      </Button>
      {open && (
        <Tooltip
          position="bottom-right"
          width={150}
          style={{ padding: 0, zIndex: 5001 }}
          onClose={() => {
            setOpen(false);
            setFocus();
          }}
        >
          <div data-adoption-id="frequency-options">
            <Menu
              onMenuSelect={(frequency: Frequency) => {
                onChange(frequency);
                setOpen(false);
                setFocus();
              }}
              items={_.values(Frequency).map(key => {
                return {
                  name: key,
                  text: FrequencyLabels[key],
                };
              })}
            />
          </div>
        </Tooltip>
      )}
    </View>
  );

  function setFocus() {
    if (isFocused) {
      dropdownBtnRef.current.focus();
    }
  }
}
